import React from "react";
import { RecentlyViewedSection } from "./RecentlyViewedSection.jsx";
import { BoardSection } from "./BoardSection.jsx";
import { TemplateCard } from "./TemplateCard.jsx";

/**
 * Flexible dashboard group section that can handle both regular sections and search results
 * @param {Object} props
 * @param {Object} props.categorizedBoards - Object with created and invited boards
 * @param {Array} props.recentlyViewed - Recently viewed items
 * @param {Array} props.boardTemplates - Template cards
 * @param {Function} props.handleWorkforceChange - Workforce change handler
 * @param {boolean} props.isSearchMode - Whether this is being used for search results
 * @param {string} props.searchCategory - Category for search results (created, invited, template)
 */
const MySwivuGroupSection = ({
  categorizedBoards,
  recentlyViewed,
  handleWorkforceChange,
  boardTemplates,
  onBoardAccess,
  isSearchMode = false,
  searchCategory = null
}) => {
  // If in search mode, render only the specific category
  if (isSearchMode && searchCategory) {
    const categoryConfig = {
      created: {
        title: "Boards Created by Me",
        description: "Boards you own and manage. You have full administrative control over these boards.",
        icon: <i className="fa-regular fa-crown !text-swivu-icon text-blue-600"></i>,
        boards: categorizedBoards.created || []
      },
      invited: {
        title: "Boards I Was Invited To",
        description: "Collaborative boards where you're a team member. Your access level depends on your assigned role.",
        icon: <i className="fa-light fa-user-plus !text-swivu-icon text-green-600"></i>,
        boards: categorizedBoards.invited || []
      },
      template: {
        title: "Templates",
        description: "Ready-to-use test drive templates to get you started quickly",
        icon: <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
          <i className="fa-solid fa-wand-magic-sparkles text-white !text-swivu-icon"></i>
        </div>,
        boards: boardTemplates || []
      }
    };

    const config = categoryConfig[searchCategory];
    if (!config) return null;

    // For templates in search mode, check if there are any templates to show
    if (searchCategory === 'template' && (!boardTemplates || boardTemplates.length === 0)) {
      return null;
    }

    // For template category, use special template card rendering
    if (searchCategory === 'template') {
      return (
        <div className="space-y-swivu-vertical-base">
          <div className="flex items-center gap-2">
            {config.icon}
            <div>
              <h2 className="text-2xl font-bold text-foreground">{config.title}</h2>
            </div>
          </div>

          <p className="text-swivu-base text-muted-foreground mb-swivu-base">
            {config.description}
          </p>

          <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-swivu-base">
            {config.boards.map((template) => (
              <TemplateCard
                key={template.id}
                title={template.title}
                description={template.description}
              />
            ))}
          </div>
        </div>
      );
    }

    return (
      <BoardSection
        title={config.title}
        description={config.description}
        icon={config.icon}
        boards={config.boards}
        onWorkforceActiveChange={handleWorkforceChange}
        onBoardAccess={onBoardAccess}
      />
    );
  }

  // Regular mode - show all sections
  return (
    <>
      <RecentlyViewedSection
        recentItems={recentlyViewed}
        onWorkforceActiveChange={handleWorkforceChange}
        onBoardAccess={onBoardAccess}
      />

      <BoardSection
        title="Boards Created by Me"
        description="Boards you own and manage. You have full administrative control over these boards."
        icon={<i className="fa-regular fa-crown !text-swivu-icon text-blue-600"></i>}
        boards={categorizedBoards.created}
        onWorkforceActiveChange={handleWorkforceChange}
        onBoardAccess={onBoardAccess}
      />

      <BoardSection
        title="Boards I Was Invited To"
        description="Collaborative boards where you're a team member. Your access level depends on your assigned role."
        icon={<i className="fa-light fa-user-plus !text-swivu-icon text-green-600"></i>}
        boards={categorizedBoards.invited}
        onWorkforceActiveChange={handleWorkforceChange}
        onBoardAccess={onBoardAccess}
      />

      {/* Templates Section - Only show if there are templates */}
      {boardTemplates && boardTemplates.length > 0 && (
        <div className="space-y-swivu-vertical-base">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <i className="fa-solid fa-wand-magic-sparkles text-white text-swivu-icon"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">Templates</h2>
            </div>
          </div>

          <p className="text-swivu-base text-muted-foreground mb-swivu-base">
            Ready-to-use test drive templates to get you started quickly.
          </p>

          <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-swivu-base">
            {boardTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                title={template.title}
                description={template.description}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default MySwivuGroupSection;